import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import { autoinject, transient, computedFrom } from "aurelia-framework";
import { Subscription, EventAggregator } from "aurelia-event-aggregator";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { MultiLookupModel, RightShipMultiLookupModels } from "../../../../components/multi-checked-list-selector/multi-checked-list-selector";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { DataType, EntityFilterPropertyModel, EntityMetaDataModelOperator, QueryFilter } from "../../../../../models/queries/queries.models";
import { Guid } from "../../../../../utilities/guid";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { AdvancedSearchType } from "../../../../../models/advanced-search/advanced-search.models";
import { Messages } from "../../../../../configuration/event-messages.constants";
import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { Claims } from "../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
@autoinject
@transient()
export class AdvancedSearch {


    public ageDefaults: { low: number, high: number } = { low: 0, high: 100 };
    public dwtDefaults: { low: number, high: number } = { low: 0, high: 565000 };
    public vumDefaults: { low: number, high: number } = { low: 0, high: 360 };

    public searchType: AdvancedSearchType = AdvancedSearchType.Vessel;
    public keywords: string;
    public includePreviousVesselNames: boolean;
    public vesselTypes: LookupValue[];
    public vesselStatuses: LookupValue[];
    public selectedVesselStatuses: LookupValue[];
    public selectedVesselTypes: LookupValue[];
    public countries: LookupValue[];
    public selectedCountries: LookupValue[];
    public minAge: number = this.ageDefaults.low;
    public maxAge: number = this.ageDefaults.high;
    public minDwt: number = this.dwtDefaults.low;
    public maxDwt: number = this.dwtDefaults.high;
    public minVesselsUnderManagement: number = this.vumDefaults.low;
    public maxVesselsUnderManagement: number = this.vumDefaults.high;
    public canAccessCompanies: boolean = false;
    public canAccessVessels: boolean = false;
    public showSafetyScore: boolean = false;
    public showGHG: boolean = false;
    public showRightShipInspectionValiditiesFilter: boolean = false;

    public selectedRightShipInspectionValidities: number[];

    constructor(private router: Router, private dialogClientService: DialogClientService, private lookupClientService: LookupClientService,
        private cacheClient: CacheClientService, private userClaimsService: UserClaimsService, private i18N: I18N, private eventAggregator: EventAggregator) {
        this.vesselStatusDisplayText = this.i18N.tr('vessel-status-text');
        this.vesselTypeDisplayText = this.i18N.tr('vessel-type');
    }

    activate(model: { keywords: string }) {
        this.showSafetyScore = this.userClaimsService.hasPermission(Claims.ShowSafetyScore);
        this.showGHG = this.userClaimsService.hasPermission(Claims.ShowGHG);

        this.canAccessCompanies = this.userClaimsService.hasPermission(Claims.CanAccessCompanies);
        this.canAccessVessels = this.userClaimsService.hasPermission(Claims.CanAccessVessels);

        window.addEventListener('keydown', this.onKeyDown, false);

        this.clear();

        const text = model && model.keywords ? model.keywords : "";
        this.keywords = $('<div/>').html(text).text();
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VesselStatuses;
        this.lookupClientService.getLookup(request).then(x => {
            this.vesselStatuses = x.activeValues.filter(x => x.isActive);
        });
        const request2 = new LookupValueRequest();
        request2.lookupValueQuery = LookupValueQuery.VesselTypes;
        this.lookupClientService.getLookup(request2).then(x => {
            this.vesselTypes = x.activeValues.filter(x => x.isActive);
        });

        this.lookupClientService.getLookup(new LookupValueRequest(LookupValueQuery.Countries)).then(x => {
            this.countries = x.activeValues;
        });
    }

    _subscriptionToDocumentClickEvent: Subscription;

    attached() {
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closeFilters.bind(this));
    }

    detached() {
        window.removeEventListener('keydown', this.onKeyDown, false);
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    onKeyDown = ($event: KeyboardEvent) => {
        if ($event.key == "Enter") {
            this.search();
        }
    }

    changeSearchType(searchType: AdvancedSearchType) {
        this.searchType = searchType;
    }

    clear() {
        this.keywords = "";
        this.selectedCountries = [];
        this.minVesselsUnderManagement = this.vumDefaults.low;
        this.maxVesselsUnderManagement = this.vumDefaults.high;
        this.includePreviousVesselNames = false;
        this.maxAge = this.ageDefaults.high;
        this.minAge = this.ageDefaults.low;
        this.minDwt = this.dwtDefaults.low;
        this.maxDwt = this.dwtDefaults.high;
        this.selectedVesselStatuses = [];
        this.selectedVesselTypes = [];
        this.selectedRightShipInspectionValidities = [];
        this.selectedGhgRatingWeights = [];
        this.selectedSafetyRatings = [];
        this.clearVesselStatusFilter(null);
        this.clearVesselTypeFilter(null);
    }

    async search() {
        if (!this.filtersValid()) return;

        const routeName = this.isVessel ? RightShipQiRouterConstants.RouteNames.VesselsSearch : this.isCompany ? RightShipQiRouterConstants.RouteNames.CompaniesSearch : null;
        if (!routeName) return;

        const filters = this.generateFilters();
        await this.cacheClient.put(`search-keywords-${routeName}`, this.keywords, CacheLocation.LocalStorage);
        await this.cacheClient.put(`search-filters-${routeName}`, filters, CacheLocation.LocalStorage);
        this.router.navigateToRoute(routeName, { t: new Date().getTime() }, { replace: true, trigger: true });
    }

    generateFilters() {
        const filters: QueryFilter[] = [];

        const inOperator = <EntityMetaDataModelOperator>{
            translatedSymbol: "{0} in ({1})",
            friendlyName: "in",
            requiresFilterValue: true,
            requiresInterpolation: true
        };

        if (this.isVessel) {
            const guid = Guid.generateGuid();
            if (this.keywords) {
                filters.push(QueryFilter.getQuery("name", this.keywords, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
                filters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.keywords, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
                filters.push(QueryFilter.getQuery("vehicleIdentificationNumber", this.keywords, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
                filters.push(QueryFilter.getQuery("europeanNumberOfIdentification", this.keywords, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
                filters.push(QueryFilter.getQuery("previousName", this.includePreviousVesselNames ? this.keywords : null, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            }

            const ageFilterActive = this.maxAge < this.ageDefaults.high || this.minAge > this.ageDefaults.low
            if (ageFilterActive) {
                const ageQ1 = QueryFilter.getQuery("age", this.minAge, "ge", DataType.Number, true);
                ageQ1.skipOnNullOrEmpty = true;
                const ageQ2 = QueryFilter.getQuery("age", this.maxAge, "le", DataType.Number, true);
                ageQ2.skipOnNullOrEmpty = true;
                filters.push(ageQ1);
                filters.push(ageQ2);
            }

            const dwtFilterActive = this.maxDwt < this.dwtDefaults.high || this.minDwt > this.dwtDefaults.low
            if (dwtFilterActive) {
                const dwtQ1 = QueryFilter.getQuery("dwt", this.minDwt, "ge", DataType.Number, true);
                dwtQ1.skipOnNullOrEmpty = true;
                const dwtQ2 = QueryFilter.getQuery("dwt", this.maxDwt, "le", DataType.Number, true);
                dwtQ2.skipOnNullOrEmpty = true;
                filters.push(dwtQ1);
                filters.push(dwtQ2);
            }

            if (this.vesselTypeFilterActive) {
                this.mclFilters[LookupValueQuery.VesselTypes].forEach(vtf => {
                    filters.push(vtf);
                });
            }

            if (this.vesselStatusFilterActive) {
                this.mclFilters[LookupValueQuery.VesselStatuses].forEach(vtf => {
                    filters.push(vtf);
                });
            }

            //rs inspection filter
            const vesselRSInspectionProperty = <EntityFilterPropertyModel>{
                name: "inspectionState",
                isDynamicProperty: false,
                type: DataType.Lookup,
                lookupMappedToProperty: LookupValueQuery.InspectionStates
            };

            filters.push(<QueryFilter>{
                filterOperator: inOperator,
                filterProperty: vesselRSInspectionProperty,
                filterValue: this.selectedRightShipInspectionValidities || [],
                isAnd: true,
                skipOnNullOrEmpty: true
            });

            //ghg rating filter
            if (this.ghgRatingFilterActive) {
                const ghgRatingProperty = <EntityFilterPropertyModel>{
                    name: "ghgRatingWeight",
                    isDynamicProperty: false,
                    type: DataType.Number
                };

                filters.push(<QueryFilter>{
                    filterOperator: inOperator,
                    filterProperty: ghgRatingProperty,
                    filterValue: this.selectedGhgRatingWeights || [],
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                    ["ghgSelectedItems"]: this.ghgSelectedItems
                });
            }

            //safety rating filter
            if (this.safetyRatingFilterActive) {
                const guid = Guid.generateGuid();
                const hasSafetyRating: boolean = this.selectedSafetyRatings.filter(x => x >= 0).length > 0;
                if (hasSafetyRating) {
                    const safetyRatingFilter: QueryFilter = {
                        filterOperator: {
                            translatedSymbol: "{0} in ({1})",
                            friendlyName: "in",
                            requiresFilterValue: true,
                            requiresInterpolation: true
                        },
                        filterProperty: {
                            name: "safetyRating",
                            isDynamicProperty: false,
                            type: DataType.Number,
                            isNullable: true,
                        },
                        filterValue: this.selectedSafetyRatings.filter(x => x >= 0),
                        isAnd: true,
                        skipOnNullOrEmpty: true,
                        groupGuid: guid
                    };
                    filters.push(safetyRatingFilter);
                }

                const isIncludeNa: boolean = this.selectedSafetyRatings.indexOf(-1) >= 0;

                const naFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: isIncludeNa ? "{0} in ({1})" : "ne",
                        friendlyName: isIncludeNa ? "in" : "<>",
                        requiresFilterValue: true,
                        requiresInterpolation: isIncludeNa
                    },
                    filterProperty: {
                        name: "safetyRatingType",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: isIncludeNa ? [-1] : -1,
                    isAnd: isIncludeNa ? (hasSafetyRating ? false : true) : true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };
                filters.push(naFilter);
            }

        } else if (this.isCompany) {

            if (this.keywords) {
                const guid = Guid.generateGuid();
                filters.push(QueryFilter.getQuery("fullName", this.keywords, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
                const filterOwCode = QueryFilter.getQuery("owCode", this.keywords, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true);
                filters.push(filterOwCode);
            }

            if (this.selectedCountries && this.selectedCountries.length > 0) {
                const countryIdProperty = <EntityFilterPropertyModel>{
                    name: "countryId",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    lookupMappedToProperty: LookupValueQuery.Countries
                };

                //countries
                filters.push(<QueryFilter>{
                    filterOperator: inOperator,
                    filterProperty: countryIdProperty,
                    filterValue: this.selectedCountries.map(a => a.id) || [],
                    isAnd: true,
                    skipOnNullOrEmpty: true
                });
            }


            if (this.vesselUnderManagementActive) {
                filters.push(QueryFilter.getQuery("docCount", this.minVesselsUnderManagement, "ge", DataType.Number, true));
                filters.push(QueryFilter.getQuery("docCount", this.maxVesselsUnderManagement, "le", DataType.Number, true));
            }
        }

        return filters;
    }

    //#region Multi Checked List dialog filters
    public cachedMultipleLookups: { [lookup: string]: MultiLookupModel[] } = {};
    public mclFilters: { [lookup: string]: QueryFilter[] } = {};

    //#region vessel type filter
    public vesselTypeDisplayText: string;
    public vesselTypeDescription: string = "";

    public clearVesselTypeFilter(event: Event) {
        if (event) event.stopPropagation();
        const lookup = LookupValueQuery.VesselTypes;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselTypeDescription = "";
        this.vesselTypeDisplayText = this.i18N.tr('vessel-type');
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselTypes", "mclFilters.RS_VesselTypes.length")
    public get vesselTypeFilterActive() {
        const lookup = LookupValueQuery.VesselTypes;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    //#region vessel status filter
    public vesselStatusDisplayText: string;
    public vesselStatusDescription: string = "";

    public clearVesselStatusFilter(event: Event) {
        if (event) event.stopPropagation();
        const lookup = LookupValueQuery.VesselStatuses;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselStatusDescription = "";
        this.vesselStatusDisplayText = this.i18N.tr('vessel-status-text');
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselStatuses", "mclFilters.RS_VesselStatuses.length")
    public get vesselStatusFilterActive() {
        const lookup = LookupValueQuery.VesselStatuses;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    openMCLDialog = (type: string) => {
        let lookup = null;
        if (type == "vessel_types")
            lookup = LookupValueQuery.VesselTypes;
        else if (type == "vessel_statuses")
            lookup = LookupValueQuery.VesselStatuses;
        else {
            console.error("invalid multi filter type")
        }
        const displayText = type == "vessel_types" ? this.i18N.tr('vessel-type') : this.i18N.tr('vessel-status-text');

        this.closeFilters();

        const cachedMultiLookups = this.cachedMultipleLookups[lookup] ? this.cachedMultipleLookups[lookup] : RightShipMultiLookupModels.getMultiLookupModel(lookup);

        let filterValues: { [filterProperty: string]: number[] } = null;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0) {
            filterValues = {};
            this.mclFilters[lookup].forEach(x => {
                filterValues[x.filterProperty.name] = x.filterValue;
            });
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MultiCheckedListDialog, {
            multiLookups: cachedMultiLookups,
            title: displayText,
            selectedIds: filterValues,
            onApply: (multiLookups: MultiLookupModel[]) => {

                this.mclFilters[lookup] = [];

                const result = RightShipMultiLookupModels.getMultiLookupFilters(lookup, displayText, multiLookups);

                this.mclFilters[lookup] = result.filters;

                if (lookup == LookupValueQuery.VesselTypes) {
                    this.vesselTypeDisplayText = result.filterDisplayText;
                    this.vesselTypeDescription = result.filterDescription;
                }
                if (lookup == LookupValueQuery.VesselStatuses) {
                    this.vesselStatusDisplayText = result.filterDisplayText;
                    this.vesselStatusDescription = result.filterDescription;
                }

                this.cachedMultipleLookups[lookup] = multiLookups;
            }
        }, true, false, false, 'right');
    }
    //#endregion

    //#region ghg rating filter
    public selectedGhgRatingWeights: number[] = [];
    public showGhgRatingFilter: boolean = false;
    public ghgSelectedItems: string[] = [];

    public toggleGhgRatingFilters($event) {
        if ($event) {
            $event.stopPropagation();
        }
        if (!this.showGhgRatingFilter) {
            this.closeFilters();
        }
        this.showGhgRatingFilter = !this.showGhgRatingFilter;
    }

    public clearGhgRatingFilter($event) {
        if ($event) {
            $event.stopPropagation();
        }
        this.selectedGhgRatingWeights = [];
    }

    @computedFrom("selectedGhgRatingWeights", "selectedGhgRatingWeights.length")
    public get ghgRatingFilterActive() {
        return this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0;
    }
    //#endregion

    //#region safety rating filter
    public selectedSafetyRatings: number[] = [];
    public showSafetyRatingFilter: boolean = false;

    public toggleSafetyRatingFilters($event) {
        if ($event) {
            $event.stopPropagation();
        }
        if (!this.showSafetyRatingFilter) {
            this.closeFilters();
        }
        this.showSafetyRatingFilter = !this.showSafetyRatingFilter;
    }

    public clearSafetyRatingFilter($event) {
        if ($event) {
            $event.stopPropagation();
        }
        this.selectedSafetyRatings = [];
    }

    @computedFrom("selectedSafetyRatings", "selectedSafetyRatings.length")
    public get safetyRatingFilterActive() {
        return this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0;
    }
    //#endregion

    //#region Vessel under management filter

    @computedFrom("minVesselsUnderManagement", "maxVesselsUnderManagement")
    public get vesselUnderManagementActive() {
        return this.minVesselsUnderManagement > this.vumDefaults.low || this.maxVesselsUnderManagement < this.vumDefaults.high
    }

    //#endregion

    filterClick($event) {
        if ($event) {
            $event.stopPropagation();
        }
    }

    public filtersValid(): boolean {

        if (this.isVessel) {
            const ageFilterValid = this.minAge <= this.maxAge
            const dwtFilterValid = this.minDwt <= this.maxDwt
            return ageFilterValid && dwtFilterValid
        }

        if (this.isCompany) {
            const vumFilterValid = this.minVesselsUnderManagement <= this.maxVesselsUnderManagement
            return vumFilterValid
        }

        return false
    }

    @computedFrom("searchType")
    public get isVessel() {
        return this.searchType == 0;

    }

    @computedFrom("searchType")
    public get isCompany() {
        return this.searchType == 1;
    }

    closeFilters() {
        this.showGhgRatingFilter = false;
        this.showSafetyRatingFilter = false;
        this.showRightShipInspectionValiditiesFilter = false;
    }
}
